import React, { useState, useCallback } from 'react';
import './index.css';

// Backend Fastify local
const API_BASE_URL = 'http://localhost:3001';

// Exemples pré-définis pour la nouvelle architecture intent-driven
const EXAMPLES = [
  {
    name: "🍷 Prendre un verre à Marly-le-Roi",
    request: {
      userQuery: "prendre un verre à Marly-le-Roi",
      lat: 48.8664,
      lng: 2.0969,
      radiusKm: 10
    }
  },
  {
    name: "🍺 Bar sympa Marly-le-Roi",
    request: {
      userQuery: "bar sympa Marly-le-Roi",
      lat: 48.8664,
      lng: 2.0969,
      radiusKm: 10
    }
  },
  {
    name: "🎵 Concert ce soir à Lille",
    request: {
      userQuery: "concert ce soir à Lille",
      lat: 50.6292,
      lng: 3.0573,
      radiusKm: 10
    }
  },
  {
    name: "🖼️ Expo demain autour de moi (Paris)",
    request: {
      userQuery: "expo demain autour de moi",
      lat: 48.8566,
      lng: 2.3522,
      radiusKm: 15
    }
  },
  {
    name: "🏓 Club de tennis de table à Marly-le-Roi",
    request: {
      userQuery: "club de tennis de table à Marly-le-Roi",
      lat: 48.8664,
      lng: 2.0969,
      radiusKm: 10
    }
  },
];

const IntentTestPage = () => {
  const [requestJson, setRequestJson] = useState(JSON.stringify(EXAMPLES[0].request, null, 2));
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState('events');

  // Charger un exemple
  const loadExample = useCallback((example) => {
    setRequestJson(JSON.stringify(example.request, null, 2));
    setResult(null);
  }, []);

  // Exécuter le test
  const runTest = useCallback(async () => {
    setLoading(true);
    setResult(null);

    try {
      const payload = JSON.parse(requestJson);
      const endpoint = `${API_BASE_URL}/api/local-events/intent`;

      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('🎯 Test Intent-Driven Architecture');
      console.log('📍 Endpoint:', endpoint);
      console.log('📤 Payload:', payload);
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      console.log('📥 Response:', data);
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

      setResult(data);
    } catch (error) {
      console.error('❌ Error:', error);
      setResult({
        error: error.message,
        stack: error.stack,
      });
    } finally {
      setLoading(false);
    }
  }, [requestJson]);

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto', fontFamily: 'system-ui' }}>
      <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '20px' }}>
        🎯 Test Intent-Driven Architecture
      </h1>
      
      <p style={{ marginBottom: '20px', color: '#666' }}>
        Test du nouveau service <code>/api/local-events/intent</code> avec architecture basée sur l'intention utilisateur.
      </p>

      {/* Exemples */}
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '10px' }}>
          📚 Exemples pré-définis :
        </h3>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {EXAMPLES.map((example, idx) => (
            <button
              key={idx}
              onClick={() => loadExample(example)}
              style={{
                padding: '8px 16px',
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              {example.name}
            </button>
          ))}
        </div>
      </div>

      {/* Request JSON */}
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '10px', color: '#2c5282' }}>
          📤 Request JSON :
        </h3>
        <textarea
          value={requestJson}
          onChange={(e) => setRequestJson(e.target.value)}
          style={{
            width: '100%',
            minHeight: '200px',
            padding: '12px',
            fontFamily: 'monospace',
            fontSize: '13px',
            border: '1px solid #cbd5e0',
            borderRadius: '6px',
            resize: 'vertical',
          }}
        />
      </div>

      {/* Bouton Run */}
      <button
        onClick={runTest}
        disabled={loading}
        style={{
          padding: '12px 24px',
          background: loading ? '#cbd5e0' : '#10b981',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          cursor: loading ? 'not-allowed' : 'pointer',
          fontSize: '16px',
          fontWeight: '600',
          marginBottom: '30px',
        }}
      >
        {loading ? '⏳ Recherche en cours...' : '🚀 Run Test'}
      </button>

      {/* Résultats */}
      {result && (
        <div style={{ marginTop: '30px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '20px' }}>
            📊 Résultats
          </h2>

          {result.error ? (
            <div style={{
              padding: '20px',
              background: '#fee2e2',
              border: '1px solid #fca5a5',
              borderRadius: '8px',
              color: '#991b1b',
            }}>
              <h3 style={{ fontWeight: '600', marginBottom: '10px' }}>❌ Erreur</h3>
              <pre style={{ fontSize: '13px', whiteSpace: 'pre-wrap' }}>{result.error}</pre>
            </div>
          ) : (
            <>
              {/* Tabs */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '2px solid #e5e7eb' }}>
                <button
                  onClick={() => setActiveTab('summary')}
                  style={{
                    padding: '10px 20px',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: activeTab === 'summary' ? '2px solid #3b82f6' : 'none',
                    color: activeTab === 'summary' ? '#3b82f6' : '#6b7280',
                    fontWeight: activeTab === 'summary' ? '600' : 'normal',
                    cursor: 'pointer',
                  }}
                >
                  📋 Résumé
                </button>
                <button
                  onClick={() => setActiveTab('events')}
                  style={{
                    padding: '10px 20px',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: activeTab === 'events' ? '2px solid #3b82f6' : 'none',
                    color: activeTab === 'events' ? '#3b82f6' : '#6b7280',
                    fontWeight: activeTab === 'events' ? '600' : 'normal',
                    cursor: 'pointer',
                  }}
                >
                  📅 Events ({result.events?.length || 0})
                </button>
                <button
                  onClick={() => setActiveTab('places')}
                  style={{
                    padding: '10px 20px',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: activeTab === 'places' ? '2px solid #3b82f6' : 'none',
                    color: activeTab === 'places' ? '#3b82f6' : '#6b7280',
                    fontWeight: activeTab === 'places' ? '600' : 'normal',
                    cursor: 'pointer',
                  }}
                >
                  📍 Places ({result.places?.length || 0})
                </button>
                <button
                  onClick={() => setActiveTab('social')}
                  style={{
                    padding: '10px 20px',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: activeTab === 'social' ? '2px solid #3b82f6' : 'none',
                    color: activeTab === 'social' ? '#3b82f6' : '#6b7280',
                    fontWeight: activeTab === 'social' ? '600' : 'normal',
                    cursor: 'pointer',
                  }}
                >
                  📱 Social ({result.otherLinks?.filter(l => l.type.startsWith('SOCIAL')).length || 0})
                </button>
                <button
                  onClick={() => setActiveTab('otherLinks')}
                  style={{
                    padding: '10px 20px',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: activeTab === 'otherLinks' ? '2px solid #3b82f6' : 'none',
                    color: activeTab === 'otherLinks' ? '#3b82f6' : '#6b7280',
                    fontWeight: activeTab === 'otherLinks' ? '600' : 'normal',
                    cursor: 'pointer',
                  }}
                >
                  🔗 Other Links ({result.otherLinks?.filter(l => !l.type.startsWith('SOCIAL')).length || 0})
                </button>
                <button
                  onClick={() => setActiveTab('json')}
                  style={{
                    padding: '10px 20px',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: activeTab === 'json' ? '2px solid #3b82f6' : 'none',
                    color: activeTab === 'json' ? '#3b82f6' : '#6b7280',
                    fontWeight: activeTab === 'json' ? '600' : 'normal',
                    cursor: 'pointer',
                  }}
                >
                  🔧 JSON Raw
                </button>
              </div>

              {/* Tab Content */}
              {activeTab === 'summary' && (
                <div style={{
                  padding: '20px',
                  background: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '15px' }}>
                    Intent détecté : <span style={{ color: '#3b82f6' }}>{result.intent}</span>
                  </h3>
                  
                  {result.constraints && (
                    <div style={{ marginBottom: '15px' }}>
                      <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Contraintes :</h4>
                      <pre style={{ fontSize: '13px', background: 'white', padding: '10px', borderRadius: '4px' }}>
                        {JSON.stringify(result.constraints, null, 2)}
                      </pre>
                    </div>
                  )}

                  {result.debug && (
                    <div>
                      <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Debug Info :</h4>
                      <ul style={{ fontSize: '13px', marginLeft: '20px' }}>
                        <li>Sources consultées : {result.debug.sourcesConsulted?.join(', ')}</li>
                        <li>Total fetched : {result.debug.totalFetched}</li>
                        <li>Total filtered : {result.debug.totalFiltered}</li>
                        <li>Scoring applied : {result.debug.scoringApplied ? 'Oui' : 'Non'}</li>
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'events' && (
                <div>
                  {result.events && result.events.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      {result.events.map((event, idx) => (
                        <div
                          key={idx}
                          style={{
                            padding: '15px',
                            background: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                            <div style={{ flex: 1 }}>
                              <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                                {event.title}
                              </h4>
                              <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '5px' }}>
                                📅 {new Date(event.startDateTime).toLocaleString('fr-FR')}
                                {event.endDateTime && ` - ${new Date(event.endDateTime).toLocaleString('fr-FR')}`}
                              </div>
                              {event.venueName && (
                                <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '5px' }}>
                                  📍 {event.venueName}
                                  {event.city && `, ${event.city}`}
                                </div>
                              )}
                              {event.description && (
                                <p style={{ fontSize: '13px', color: '#4b5563', marginTop: '8px' }}>
                                  {event.description}
                                </p>
                              )}
                            </div>
                            <div style={{ marginLeft: '15px', textAlign: 'right' }}>
                              <span style={{
                                padding: '4px 8px',
                                background: '#dbeafe',
                                color: '#1e40af',
                                fontSize: '12px',
                                borderRadius: '4px',
                                display: 'inline-block',
                                marginBottom: '5px',
                              }}>
                                {event.category}
                              </span>
                              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                {event.source.provider}
                              </div>
                              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                ⭐ {event.source.confidenceScore.toFixed(2)}
                              </div>
                            </div>
                          </div>
                          {event.source.url && (
                            <a
                              href={event.source.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                fontSize: '13px',
                                color: '#3b82f6',
                                textDecoration: 'none',
                                marginTop: '10px',
                                display: 'inline-block',
                              }}
                            >
                              🔗 Voir la source →
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{
                      padding: '40px',
                      textAlign: 'center',
                      color: '#6b7280',
                      background: '#f9fafb',
                      borderRadius: '8px',
                    }}>
                      Aucun événement trouvé
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'places' && (
                <div>
                  {result.places && result.places.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      {result.places.map((place, idx) => (
                        <div
                          key={idx}
                          style={{
                            padding: '15px',
                            background: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                            <div style={{ flex: 1 }}>
                              <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                                {place.name || 'Lieu (nom indisponible)'}
                              </h4>
                              {(place.address || place.city) && (
                                <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '5px' }}>
                                  📍 {place.address}
                                  {place.city && (place.address ? `, ${place.city}` : place.city)}
                                </div>
                              )}
                              {place.rating && (
                                <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '5px' }}>
                                  ⭐ {place.rating.toFixed(1)}
                                  {place.reviewCount && ` (${place.reviewCount} avis)`}
                                </div>
                              )}
                              {place.phone && (
                                <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '5px' }}>
                                  📞 {place.phone}
                                </div>
                              )}
                              {place.openingHours && (
                                <div style={{ fontSize: '13px', color: '#4b5563', marginTop: '8px' }}>
                                  🕐 {place.openingHours}
                                </div>
                              )}
                            </div>
                            <div style={{ marginLeft: '15px', textAlign: 'right' }}>
                              {place.score !== undefined && (
                                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>
                                  Score: {place.score}
                                </div>
                              )}
                            </div>
                          </div>
                          <div style={{ marginTop: '10px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            {place.website && (
                              <a
                                href={place.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  fontSize: '13px',
                                  color: '#3b82f6',
                                  textDecoration: 'none',
                                }}
                              >
                                🌐 Site web →
                              </a>
                            )}
                            {place.mapsLink && (
                              <a
                                href={place.mapsLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  fontSize: '13px',
                                  color: '#3b82f6',
                                  textDecoration: 'none',
                                }}
                              >
                                🗺️ Google Maps →
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{
                      padding: '40px',
                      textAlign: 'center',
                      color: '#6b7280',
                      background: '#f9fafb',
                      borderRadius: '8px',
                    }}>
                      Aucun lieu trouvé
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'social' && (
                <div>
                  {result.otherLinks && result.otherLinks.filter(l => l.type.startsWith('SOCIAL')).length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {result.otherLinks.filter(l => l.type.startsWith('SOCIAL')).map((link, idx) => (
                        <div
                          key={idx}
                          style={{
                            padding: '15px',
                            background: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                            <div style={{ flex: 1 }}>
                              <h4 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '5px' }}>
                                {link.title}
                              </h4>
                              <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>
                                {link.hint}
                              </div>
                              <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                                {link.url.includes('facebook') && '📘 Facebook'}
                                {link.url.includes('instagram') && '📸 Instagram'}
                                {link.url.includes('twitter') && '🐦 Twitter'}
                              </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', alignItems: 'flex-end' }}>
                              <span style={{
                                padding: '4px 8px',
                                background: link.type === 'SOCIAL_OFFICIAL_PAGE' ? '#dbeafe' : 
                                           link.type === 'SOCIAL_PUBLIC_POST' ? '#fef3c7' : 
                                           '#e0e7ff',
                                color: link.type === 'SOCIAL_OFFICIAL_PAGE' ? '#1e40af' : 
                                       link.type === 'SOCIAL_PUBLIC_POST' ? '#92400e' : 
                                       '#4338ca',
                                fontSize: '11px',
                                borderRadius: '4px',
                                fontWeight: '600',
                              }}>
                                {link.type === 'SOCIAL_OFFICIAL_PAGE' && '✓ OFFICIEL'}
                                {link.type === 'SOCIAL_PUBLIC_POST' && '📝 POST'}
                                {link.type === 'SOCIAL_COMMUNITY_GROUP' && '👥 GROUPE'}
                                {link.type === 'SOCIAL' && '📱 SOCIAL'}
                              </span>
                            </div>
                          </div>
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              fontSize: '13px',
                              color: '#3b82f6',
                              textDecoration: 'none',
                              marginTop: '10px',
                              display: 'inline-block',
                            }}
                          >
                            🔗 Voir sur {link.url.includes('facebook') ? 'Facebook' : link.url.includes('instagram') ? 'Instagram' : 'le réseau'} →
                          </a>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{
                      padding: '40px',
                      textAlign: 'center',
                      color: '#6b7280',
                      background: '#f9fafb',
                      borderRadius: '8px',
                    }}>
                      Aucun lien social trouvé
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'otherLinks' && (
                <div>
                  {result.otherLinks && result.otherLinks.filter(l => !l.type.startsWith('SOCIAL')).length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {result.otherLinks.filter(l => !l.type.startsWith('SOCIAL')).map((link, idx) => (
                        <div
                          key={idx}
                          style={{
                            padding: '15px',
                            background: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <div>
                              <h4 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '5px' }}>
                                {link.title}
                              </h4>
                              <div style={{ fontSize: '13px', color: '#6b7280' }}>
                                {link.hint}
                              </div>
                            </div>
                            <span style={{
                              padding: '4px 8px',
                              background: '#fef3c7',
                              color: '#92400e',
                              fontSize: '12px',
                              borderRadius: '4px',
                              height: 'fit-content',
                            }}>
                              {link.type}
                            </span>
                          </div>
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              fontSize: '13px',
                              color: '#3b82f6',
                              textDecoration: 'none',
                              marginTop: '10px',
                              display: 'inline-block',
                            }}
                          >
                            🔗 {link.url} →
                          </a>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{
                      padding: '40px',
                      textAlign: 'center',
                      color: '#6b7280',
                      background: '#f9fafb',
                      borderRadius: '8px',
                    }}>
                      Aucun lien autre trouvé
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'json' && (
                <pre style={{
                  padding: '20px',
                  background: '#1f2937',
                  color: '#f9fafb',
                  borderRadius: '8px',
                  fontSize: '13px',
                  overflow: 'auto',
                  maxHeight: '600px',
                }}>
                  {JSON.stringify(result, null, 2)}
                </pre>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default IntentTestPage;

